(() => {
  const ROOT_SELECTOR = '[data-fd-masonry-gallery]';
  const OPEN_CLASS = 'fd-gallery-lightbox-open';

  const initGallery = (gallery) => {
    if (gallery.dataset.fdMasonryReady === 'true') return;

    const items = Array.from(gallery.querySelectorAll('[data-fd-gallery-item]'));
    const grid = gallery.querySelector('[data-fd-masonry-grid]');
    const triggers = Array.from(gallery.querySelectorAll('[data-fd-gallery-trigger]'));
    const toggle = gallery.querySelector('[data-fd-gallery-toggle]');
    const toggleLabel = gallery.querySelector('[data-fd-gallery-toggle-label]');
    const modal = gallery.querySelector('[data-fd-gallery-lightbox]');
    const modalImage = gallery.querySelector('[data-fd-gallery-lightbox-image]');
    const closeButton = gallery.querySelector('[data-fd-gallery-close]');
    const previousButton = gallery.querySelector('[data-fd-gallery-previous]');
    const nextButton = gallery.querySelector('[data-fd-gallery-next]');
    const controller = new AbortController();
    const signal = controller.signal;
    let activeTrigger = null;
    let revealObserver = null;
    let currentColumnCount = 0;
    let resizeTimer = null;

    gallery.classList.add('fd-masonry-gallery--enhanced');

    const getColumnCount = () => {
      if (window.innerWidth < 750) return 2;
      if (window.innerWidth < 990) return 3;
      if (window.innerWidth < 1200) return 4;
      return 5;
    };

    const buildColumns = () => {
      if (!grid) return;
      const columnCount = getColumnCount();
      if (columnCount === currentColumnCount && grid.querySelector('.fd-masonry-gallery__column')) return;

      currentColumnCount = columnCount;
      const columns = Array.from({ length: columnCount }, () => {
        const column = document.createElement('div');
        column.className = 'fd-masonry-gallery__column';
        return column;
      });

      items.forEach((item, index) => {
        const columnIndex = index % columnCount;
        const rowIndex = Math.floor(index / columnCount);
        const isTall = (rowIndex + columnIndex) % 2 === 1;
        item.classList.toggle('fd-masonry-gallery__item--tall', isTall);
        item.classList.toggle('fd-masonry-gallery__item--short', !isTall);
        columns[columnIndex].appendChild(item);
      });

      grid.style.setProperty('--fd-gallery-columns', String(columnCount));
      grid.replaceChildren(...columns);
    };

    buildColumns();

    if ('IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -7% 0px', threshold: 0.04 });
      items.forEach((item) => revealObserver.observe(item));
    } else {
      items.forEach((item) => item.classList.add('is-visible'));
    }

    const availableTriggers = () => triggers.filter((trigger) => trigger.offsetParent !== null);

    const renderActiveImage = (trigger) => {
      if (!trigger || !modalImage) return;
      modalImage.src = trigger.dataset.gallerySrc;
      modalImage.alt = trigger.dataset.galleryAlt || '';
      activeTrigger = trigger;
    };

    const openModal = (trigger) => {
      if (!modal || !modalImage) return;
      renderActiveImage(trigger);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add(OPEN_CLASS);
      document.body.classList.add(OPEN_CLASS);
      requestAnimationFrame(() => {
        modal.classList.add('is-open');
        closeButton?.focus();
      });
    };

    const closeModal = () => {
      if (!modal || modal.hidden) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove(OPEN_CLASS);
      document.body.classList.remove(OPEN_CLASS);
      window.setTimeout(() => {
        modal.hidden = true;
        activeTrigger?.focus();
      }, 200);
    };

    const showAdjacent = (direction) => {
      const visibleTriggers = availableTriggers();
      if (!visibleTriggers.length) return;
      const currentIndex = Math.max(0, visibleTriggers.indexOf(activeTrigger));
      const nextIndex = (currentIndex + direction + visibleTriggers.length) % visibleTriggers.length;
      renderActiveImage(visibleTriggers[nextIndex]);
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => openModal(trigger), { signal });
    });

    closeButton?.addEventListener('click', closeModal, { signal });
    previousButton?.addEventListener('click', () => showAdjacent(-1), { signal });
    nextButton?.addEventListener('click', () => showAdjacent(1), { signal });

    modal?.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    }, { signal });

    modal?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeModal();
        return;
      }
      if (event.key === 'ArrowLeft') {
        showAdjacent(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        showAdjacent(1);
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [closeButton, previousButton, nextButton].filter(Boolean);
      const currentIndex = focusable.indexOf(document.activeElement);
      if (event.shiftKey && currentIndex <= 0) {
        event.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (!event.shiftKey && currentIndex === focusable.length - 1) {
        event.preventDefault();
        focusable[0].focus();
      }
    }, { signal });

    toggle?.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') !== 'true';
      gallery.classList.toggle('is-expanded', expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      if (toggleLabel) {
        toggleLabel.textContent = expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel;
      }

      if (expanded && revealObserver) {
        items.forEach((item) => {
          if (!item.classList.contains('is-visible')) revealObserver.observe(item);
        });
      }

      if (!expanded) {
        items.slice(Number(gallery.dataset.mobileLimit) || 10).forEach((item) => item.classList.remove('is-visible'));
        gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, { signal });

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(buildColumns, 120);
    }, { signal });

    gallery.fdMasonryCleanup = () => {
      controller.abort();
      window.clearTimeout(resizeTimer);
      revealObserver?.disconnect();
      document.documentElement.classList.remove(OPEN_CLASS);
      document.body.classList.remove(OPEN_CLASS);
      delete gallery.dataset.fdMasonryReady;
    };
    gallery.dataset.fdMasonryReady = 'true';
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initGallery(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(ROOT_SELECTOR)) event.target.fdMasonryCleanup?.();
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((gallery) => gallery.fdMasonryCleanup?.());
  });
})();
